import { Box, Button, Group, Stack, Text, Title } from '@mantine/core';

import RawModelList from '@/app/assets/components/upload/molecules/RawModelList';
import AssetsDropzone from '@/app/assets/components/upload/atoms/AssetsDropzone';
import { FileValidationErrors } from '@/app/assets/components/upload/hooks/useFileValidation';
import { FileWithPath } from '@mantine/dropzone';
import StorageIndicator from '@/app/assets/components/upload/atoms/StorageIndicator';
import { IconBook } from '@tabler/icons-react';
import Link from 'next/link';
import externalLinks from '@/utils/externalLinks';
import { showErrorNotification } from '@/app/components/shared/notifications';
import { useUserQuery } from '@/shared/hooks/useUserQuery';
import { NewTag } from '../atoms/NewTag';
import { AssetTagBadge } from '@/app/assets/details/atoms/AssetTagBadge';
import { useAssetsUploadContext } from '../contexts/useAssetsUploadContext';

type RawAssetUploaderProps = {
  files: FileWithPath[];
  removeFile: (index: number) => void;
  removeAllFiles: () => void;
  className?: string;
  isUploadPending: boolean;
  fileValidationErrors: FileValidationErrors;
  onFileDropped: (files: FileWithPath[]) => void;
  credits: number;
  storageLeft: number;
  storageLimit: number;
};

export default function RawAssetUploader({
  files,
  removeFile,
  removeAllFiles,
  isUploadPending,
  fileValidationErrors,
  onFileDropped,
  credits,
  storageLeft,
  storageLimit,
}: RawAssetUploaderProps) {
  const { data: user } = useUserQuery();
  const { allFilesMetadata, addAllFilesTag, removeAllFilesTag } = useAssetsUploadContext();

  const allFilesTags = allFilesMetadata.tags ?? [];

  function pointsForUploadAvailable(): boolean {
    // Handle infinite overdraft case (-1)
    if (user?.plan.overdraft_limit && user.plan.overdraft_limit < 0) {
      return true;
    }

    if (user?.plan.details.import_cost === 1) {
      // Calculate the available points
      const points = (user.rapid_points ?? 0) + user.plan.overdraft_limit - user.plan.overdrawn_points;
      // Return true if points are available, otherwise false
      return points > 0;
    }

    // If not on the new plan, allow by default
    return true;
  }

  const hasInsufficientStorage = getCumulativeFilesSize(files) > storageLeft;

  function getAdditionalDropZoneProps() {
    if (!pointsForUploadAvailable()) {
      return {
        errorNotificationMessage:
          'Insufficient credits. Please adjust your overdraft limits or subscribe to a higher tier plan.',
      };
    }

    if (hasInsufficientStorage) {
      return {
        errorNotificationMessage: insufficientStorageError,
      };
    }

    return {};
  }

  return (
    <Stack justify='start' gap={0}>
      <Group justify='space-between'>
        <Title order={6} mb='xs'>
          Select files
        </Title>
        <Button
          leftSection={<IconBook style={{ width: 16 }} />}
          variant='transparent'
          size='xs'
          styles={{ label: { fontSize: 14 } }}
          component={Link}
          href={externalLinks.assetGuidelines()}
          target='_blank'
        >
          Asset Guidelines
        </Button>
      </Group>
      <Text c='gray.6' mb='md'>
        Upload or drag & drop files here to process your own assets.
      </Text>
      <AssetsDropzone
        disabled={isUploadPending || !pointsForUploadAvailable() || hasInsufficientStorage}
        onFileDropped={(files) => {
          if (getCumulativeFilesSize(files) > storageLeft) {
            return showErrorNotification({ message: insufficientStorageError });
          }
          onFileDropped(files);
        }}
        {...getAdditionalDropZoneProps()}
      />
      <Group m='sm' justify='space-between'>
        <Box>
          {credits !== 0 && (
            <Text c='gray.6'>
              You’ll use {credits} {credits === 1 ? 'RapidPoint' : 'RapidPoints'} to upload the files
            </Text>
          )}
        </Box>
        <StorageIndicator className='self-end' storageLeft={storageLeft} storageLimit={storageLimit} />
      </Group>
      <RawModelList
        onRemoveAsset={removeFile}
        validationErrors={fileValidationErrors}
        isUploadPending={isUploadPending}
      />
      {files.length > 1 && !isUploadPending && (
        <Button mt='sm' className='self-end' variant='transparent' onClick={removeAllFiles}>
          Remove all
        </Button>
      )}
      <Group mih='lg' gap='xs'>
        <Text c='gray.6'>Add Tags to all</Text>
        {allFilesTags.map((tag) => (
          <AssetTagBadge key={tag} iconClick={() => removeAllFilesTag(tag)}>
            {tag}
          </AssetTagBadge>
        ))}
        <NewTag tags={allFilesTags} onAddTag={addAllFilesTag} />
      </Group>
    </Stack>
  );
}

const getCumulativeFilesSize = (files: FileWithPath[]) => {
  return files.reduce((acc, { size }) => acc + size, 0);
};

const insufficientStorageError =
  'You have not enough storage left for your account. Please upgrade or remove data to regain storage.';
